import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  musicApi,
  type InteractiveCandidate,
} from "@/api/music";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { Status, type StatusTone } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";

/**
 * Per-album interactive search picker (issue #3).
 *
 * Auto-grab dispatches the top scoring release without showing the
 * candidate list — when the pick is wrong (wrong cut, wrong release
 * group, low-seed torrent) the user has no escape hatch. This modal
 * runs the *same* indexer fan-out and surfaces the full ranked list,
 * so the user can pick the release they actually want.
 *
 * Shape mirrors `SubtitleSearchModal` (issue #87): a sortable table
 * with score, quality, size, seeders, blocklist flag, and a per-row
 * "Grab" button that dispatches via `POST /albums/:id/grab-release`.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  albumId: number;
  /** Eyebrow / title hint, e.g. "Artist — Album". */
  albumTitle: string;
}

export function AlbumInteractiveSearchModal({
  open,
  onClose,
  albumId,
  albumTitle,
}: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();

  // `submitted` is the query string actually executed (drives the
  // react-query cache key). `draft` is the value bound to the input
  // box — they diverge while the user is typing a new query. Both
  // start `null` meaning "use the backend default `{artist} {album}`";
  // the response's `query` field then seeds the input on first paint.
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>("");

  // Reset query state every time the modal closes, so reopening for a
  // different album doesn't carry forward the previous draft. Also
  // guards against stale `submitted` if the user closes the modal
  // mid-search and reopens.
  useEffect(() => {
    if (!open) {
      setSubmitted(null);
      setDraft("");
    }
  }, [open]);

  const candidatesQ = useQuery({
    // Cache key includes `submitted` so a refined search shows its
    // own loading state instead of flashing stale candidates from the
    // previous query.
    queryKey: ["music", "album", albumId, "interactive-search", submitted],
    queryFn: () =>
      musicApi.searchAlbumInteractive(albumId, submitted ?? undefined),
    enabled: open,
    // Fresh-by-default: reopening the modal after a failed dispatch
    // shouldn't re-show stale candidates that the user already saw.
    staleTime: 0,
  });

  // Seed the input box from the resolved query the *first* time a
  // response lands. Don't keep echoing it back into the input on
  // subsequent searches — that would clobber what the user just typed
  // mid-flight if the request is slower than their keystrokes.
  useEffect(() => {
    if (candidatesQ.data?.query && draft === "") {
      setDraft(candidatesQ.data.query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesQ.data?.query]);

  const grabMut = useMutation({
    mutationFn: (c: InteractiveCandidate) =>
      musicApi.grabAlbumRelease(albumId, {
        indexer_id: c.indexer_id,
        release_title: c.release_title,
        download_url: c.download_url,
        release_guid: c.release_guid,
        size: c.size,
      }),
    onSuccess: (record) => {
      toast(
        `Grabbed ${record.release_title} via ${record.indexer_name ?? "?"} → ${record.download_client_name ?? "?"}`,
        "success",
      );
      // The album now has a queued grab; close the modal so the user
      // sees the freshly-rendered Recent grabs row on the page below.
      qc.invalidateQueries({ queryKey: ["grabs"] });
      qc.invalidateQueries({ queryKey: ["music", "album", albumId] });
      onClose();
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Grab failed", "error"),
  });

  const candidates = candidatesQ.data?.candidates ?? [];

  // Submit handler shared by the form's onSubmit and the button's
  // onClick — the form already wires Enter, but routing both through
  // the same path keeps behaviour consistent if the form ever grows.
  const runSearch = () => {
    const trimmed = draft.trim();
    // null + the empty-string fallback both mean "use the backend
    // default". Send null so the cache key stays stable across
    // empty-string variants.
    setSubmitted(trimmed.length > 0 ? trimmed : null);
  };

  // Did the resolved query come back empty for an *override* search?
  // We surface a slightly different empty-state in that case ("nothing
  // for `<your query>` — try refining") vs. the default-search miss.
  const submittedQuery = candidatesQ.data?.query ?? null;
  const isOverride = submitted !== null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Interactive search"
      eyebrow={albumTitle}
      width="xl"
    >
      <div className="space-y-4">
        <p className="text-xs text-foreground/60">
          Same indexer fan-out as auto Search & Grab. Score, quality, and
          blocklist signals are the ones the auto-pick would have used to
          rank — pick a row to override the default winner. Refine the
          query below if the default doesn't surface what you want.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            runSearch();
          }}
          className="flex flex-col gap-2 sm:flex-row sm:items-center"
        >
          <TextInput
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search query (e.g. artist album year)"
            aria-label="Search query"
            className="font-mono text-[13px]"
          />
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              size="sm"
              loading={candidatesQ.isFetching}
              disabled={candidatesQ.isFetching}
            >
              Search
            </Button>
            {/* Reset is only meaningful once the user has overridden,
                otherwise it's a no-op. Hide it in the default state to
                keep the UI quiet. */}
            {isOverride && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft("");
                  setSubmitted(null);
                }}
              >
                Reset
              </Button>
            )}
          </div>
        </form>

        {candidatesQ.isLoading || candidatesQ.isFetching ? (
          <div className="rounded-md border border-line bg-surface/30 px-4 py-10 text-center text-sm text-foreground/55">
            Searching indexers…
          </div>
        ) : candidatesQ.isError ? (
          <div className="rounded-md border border-bad/40 bg-bad/10 px-3 py-3 text-xs text-bad">
            {candidatesQ.error instanceof Error
              ? candidatesQ.error.message
              : "Search failed"}
          </div>
        ) : candidates.length === 0 && candidatesQ.isSuccess ? (
          <div className="rounded-md border border-dashed border-line bg-surface/30 px-4 py-10 text-center text-sm text-foreground/55">
            No releases found for{" "}
            <code className="font-mono text-foreground/75">
              {submittedQuery ?? "this album"}
            </code>{" "}
            across enabled indexers.
            {isOverride && (
              <div className="mt-1 text-xs text-foreground/45">
                Try a different query, or reset to the default.
              </div>
            )}
          </div>
        ) : candidates.length > 0 ? (
          <CandidatesTable
            candidates={candidates}
            onGrab={(c) => grabMut.mutate(c)}
            pendingGuid={
              grabMut.isPending ? grabMut.variables?.release_guid ?? null : null
            }
          />
        ) : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function CandidatesTable({
  candidates,
  onGrab,
  pendingGuid,
}: {
  candidates: InteractiveCandidate[];
  onGrab: (c: InteractiveCandidate) => void;
  pendingGuid: string | null;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-line bg-surface/30">
      <table className="w-full text-xs">
        <thead className="bg-surface-2/50">
          <tr className="text-left">
            {[
              "Score",
              "Quality",
              "Release",
              "Indexer",
              "Size",
              "Seeders",
              "Flags",
              "",
            ].map((h) => (
              <th
                key={h}
                className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line/60">
          {candidates.map((c, i) => (
            <CandidateRowView
              key={`${c.indexer_id}:${c.release_guid}:${i}`}
              c={c}
              onGrab={() => onGrab(c)}
              pending={pendingGuid === c.release_guid}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CandidateRowView({
  c,
  onGrab,
  pending,
}: {
  c: InteractiveCandidate;
  onGrab: () => void;
  pending: boolean;
}) {
  const tone = scoreTone(c.score);
  const qualityCmp = compareQualityForUpgrade(c.quality_slug, c.current_quality_slug);
  return (
    <tr
      className={`transition hover:bg-surface-2/40 ${
        c.blocklisted ? "bg-warn/5" : ""
      }`}
    >
      <td className="px-3 py-2.5 align-top">
        <ScoreCell score={c.score} tone={tone} />
      </td>
      <td className="px-3 py-2.5 align-top">
        <QualityCell
          slug={c.quality_slug}
          currentSlug={c.current_quality_slug}
          cmp={qualityCmp}
        />
      </td>
      <td className="max-w-md px-3 py-2.5 align-top">
        <span className="break-all font-mono text-[11px]">
          {c.release_title}
        </span>
      </td>
      <td className="px-3 py-2.5 align-top">
        <Status variant="chip" tone="neutral" className="font-mono">
          {c.indexer_name || `#${c.indexer_id}`}
        </Status>
      </td>
      <td className="px-3 py-2.5 align-top font-mono text-[11px] tabular-nums text-foreground/80">
        {formatSize(c.size)}
      </td>
      <td className="px-3 py-2.5 align-top font-mono text-[11px] tabular-nums text-foreground/80">
        {c.seeders == null ? (
          <span className="text-foreground/40">—</span>
        ) : (
          c.seeders.toLocaleString()
        )}
      </td>
      <td className="px-3 py-2.5 align-top">
        <div className="flex flex-wrap gap-1">
          {c.blocklisted && (
            <Status variant="chip" tone="warn">
              blocklisted
            </Status>
          )}
          {c.publish_date && (
            <Status variant="chip" tone="neutral">
              {formatPublishDate(c.publish_date)}
            </Status>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right align-top whitespace-nowrap">
        <Button size="sm" onClick={onGrab} loading={pending}>
          Grab
        </Button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

function ScoreCell({ score, tone }: { score: number; tone: StatusTone }) {
  const sign = score > 0 ? "+" : "";
  return (
    <span
      className={`inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-mono text-[11px] font-medium tabular-nums ${toneToScoreClass(tone)}`}
    >
      {sign}
      {score}
    </span>
  );
}

/**
 * Detected-quality chip with an upgrade-vs-downgrade indicator
 * (↑ / = / ↓) when `current_quality_slug` is set. NULL slugs render as
 * "?" so undetectable releases don't blank the column.
 */
function QualityCell({
  slug,
  currentSlug,
  cmp,
}: {
  slug: string | null;
  currentSlug: string | null;
  cmp: "upgrade" | "same" | "downgrade" | "unknown";
}) {
  const tone: StatusTone =
    cmp === "upgrade"
      ? "good"
      : cmp === "downgrade"
        ? "warn"
        : cmp === "same"
          ? "info"
          : "neutral";
  const arrow =
    cmp === "upgrade"
      ? " ↑"
      : cmp === "downgrade"
        ? " ↓"
        : cmp === "same"
          ? " ="
          : "";
  const tooltip =
    cmp === "unknown" || !currentSlug
      ? slug ?? "unknown quality"
      : `${slug ?? "?"} vs current ${currentSlug}`;
  return (
    <span title={tooltip} className="inline-block">
      <Status variant="chip" tone={tone} className="font-mono">
        {slug ?? "?"}
        {arrow}
      </Status>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function scoreTone(score: number): StatusTone {
  if (score > 100) return "good";
  if (score > 0) return "accent";
  if (score === 0) return "neutral";
  return "warn";
}

function toneToScoreClass(tone: StatusTone): string {
  switch (tone) {
    case "good":
      return "border-good/40 bg-good/10 text-good";
    case "accent":
      return "border-accent/40 bg-accent/10 text-accent";
    case "info":
      return "border-info/40 bg-info/10 text-info";
    case "warn":
      return "border-warn/40 bg-warn/10 text-warn";
    case "bad":
      return "border-bad/40 bg-bad/10 text-bad";
    default:
      return "border-line bg-surface-2 text-foreground/65";
  }
}

/**
 * Best-effort upgrade comparison. The canonical music quality ladder
 * is held server-side; this client-side approximation orders by a
 * coarse rank derived from the slug suffix so the picker can render
 * an arrow without an extra round-trip. Unknown slugs fall through
 * to "unknown" — no arrow.
 */
function compareQualityForUpgrade(
  candidate: string | null,
  current: string | null,
): "upgrade" | "same" | "downgrade" | "unknown" {
  if (!current) return "unknown";
  if (!candidate) return "unknown";
  if (candidate === current) return "same";
  const cRank = clientSideRank(candidate);
  const curRank = clientSideRank(current);
  if (cRank == null || curRank == null) return "unknown";
  if (cRank > curRank) return "upgrade";
  if (cRank < curRank) return "downgrade";
  return "same";
}

/** Coarse music-quality ranking. Higher = better. Mirrors the
 *  workspace's canonical order (lossless > 24bit > lossy-high >
 *  lossy-low) so the picker doesn't need the full catalog. */
function clientSideRank(slug: string): number | null {
  // Lossless
  if (slug === "flac-24bit") return 110;
  if (slug === "flac-lossless") return 100;
  if (slug === "alac-lossless") return 95;
  // High lossy
  if (slug === "mp3-320") return 80;
  if (slug === "aac-256") return 78;
  if (slug === "ogg-320") return 76;
  // Mid lossy
  if (slug === "mp3-256") return 70;
  if (slug === "mp3-192") return 60;
  if (slug === "aac-192") return 60;
  // Low lossy
  if (slug === "mp3-128") return 40;
  return null;
}

/** "1.23 GB" / "456 MB" / "12 KB" — picker default unit format. */
function formatSize(bytes: number): string {
  if (bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

/** Compact date for the publish-date chip. */
function formatPublishDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
